import { createSlice } from "@reduxjs/toolkit";
import {
  fetchAllNotifications,
  fetchNotifications,
  fetchUnreadPreview,
  markNotificationRead,
  markAllNotificationsRead,
  approveNotification,
  rejectNotification,
  delegateNotification,
  escalateNotification,
  deleteNotification,
  deleteAllUnreadNotifications,
} from "../actions/notificationActions";

const initialState = {
  // All notifications list
  notifications: [],
  notificationsLoading: false,
  notificationsError: null,

  // Pagination state
  pagination: {
    nextCursor: null,
    totalCount: 0,
    hasMore: false,
    pageSize: 20,
    currentCategory: null, // Current active category filter
  },

  // Unread preview for navbar
  unreadPreview: [],
  unreadCount: 0,
  previewLoading: false,

  // Selected notification for detail view
  selectedNotificationId: null,

  // Action states
  actionLoading: false,
  actionError: null,

  // WebSocket connection status
  wsConnected: false,

  // Last fetched timestamp (for polling logic)
  lastFetched: null,
};

const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    // Set selected notification
    setSelectedNotification: (state, { payload }) => {
      state.selectedNotificationId = payload;
    },

    // Clear selected notification
    clearSelectedNotification: (state) => {
      state.selectedNotificationId = null;
    },

    // WebSocket connection status
    setWsConnected: (state, { payload }) => {
      state.wsConnected = payload;
    },

    // Handle new notification from WebSocket
    addNotificationFromWs: (state, { payload }) => {
      const { notification, unread_count } = payload;

      // Add to beginning of notifications list if not already present
      const exists = state.notifications.some((n) => n.id === notification.id);
      if (!exists) {
        state.notifications.unshift(notification);
      }

      // Update unread preview (keep max 5 for navbar)
      if (!notification.isRead) {
        const previewExists = state.unreadPreview.some(
          (n) => n.id === notification.id,
        );
        if (!previewExists) {
          state.unreadPreview.unshift(notification);
          if (state.unreadPreview.length > 5) {
            state.unreadPreview = state.unreadPreview.slice(0, 5);
          }
        }
      }

      // Update unread count if provided
      if (typeof unread_count === "number") {
        state.unreadCount = unread_count;
      } else {
        state.unreadCount += 1;
      }
    },

    // Handle notification update from WebSocket
    updateNotificationFromWs: (state, { payload }) => {
      const { notification_id, updates, unread_count } = payload;

      // Update in notifications list
      const notifIndex = state.notifications.findIndex(
        (n) => n.id === notification_id,
      );
      if (notifIndex !== -1) {
        state.notifications[notifIndex] = {
          ...state.notifications[notifIndex],
          ...updates,
        };
      }

      // Update in preview if exists
      const previewIndex = state.unreadPreview.findIndex(
        (n) => n.id === notification_id,
      );
      if (previewIndex !== -1) {
        if (updates.isRead) {
          // Remove from unread preview if marked as read
          state.unreadPreview.splice(previewIndex, 1);
        } else {
          state.unreadPreview[previewIndex] = {
            ...state.unreadPreview[previewIndex],
            ...updates,
          };
        }
      }

      // Update unread count if provided
      if (typeof unread_count === "number") {
        state.unreadCount = unread_count;
      }
    },

    // Handle notification deletion from WebSocket
    removeNotificationsFromWs: (state, { payload }) => {
      const { notification_ids, unread_count } = payload;

      // Remove from notifications list
      state.notifications = state.notifications.filter(
        (n) => !notification_ids.includes(n.id),
      );

      // Remove from preview
      state.unreadPreview = state.unreadPreview.filter(
        (n) => !notification_ids.includes(n.id),
      );

      // Update unread count if provided
      if (typeof unread_count === "number") {
        state.unreadCount = unread_count;
      }
    },

    // Update unread count from WebSocket
    setUnreadCount: (state, { payload }) => {
      state.unreadCount = payload;
    },

    // Clear all notification state (e.g., on logout)
    clearNotificationState: (state) => {
      return initialState;
    },

    // Clear action error
    clearActionError: (state) => {
      state.actionError = null;
    },
  },

  extraReducers: (builder) => {
    // Fetch notifications with pagination
    builder.addCase(fetchNotifications.pending, (state, { meta }) => {
      // Only show loading for initial fetch, not for "load more"
      if (!meta.arg?.append) {
        state.notificationsLoading = true;
      }
      state.notificationsError = null;
    });
    builder.addCase(fetchNotifications.fulfilled, (state, { payload }) => {
      state.notificationsLoading = false;

      if (payload.append) {
        // Append new notifications (infinite scroll)
        const existingIds = new Set(state.notifications.map((n) => n.id));
        const newNotifications = payload.results.filter(
          (n) => !existingIds.has(n.id),
        );
        state.notifications = [...state.notifications, ...newNotifications];
      } else {
        // Replace notifications (initial load or category change)
        state.notifications = payload.results || [];
      }

      // Update pagination state
      state.pagination.nextCursor = payload.next_cursor;
      state.pagination.totalCount = payload.total_count;
      state.pagination.hasMore = payload.has_more;
      state.pagination.pageSize = payload.page_size;
      state.pagination.currentCategory = payload.category;

      state.lastFetched = new Date().toISOString();
    });
    builder.addCase(fetchNotifications.rejected, (state, { payload }) => {
      state.notificationsLoading = false;
      state.notificationsError = payload;
    });

    // Fetch all notifications (legacy - no pagination)
    builder.addCase(fetchAllNotifications.pending, (state) => {
      state.notificationsLoading = true;
      state.notificationsError = null;
    });
    builder.addCase(fetchAllNotifications.fulfilled, (state, { payload }) => {
      state.notificationsLoading = false;
      state.notifications = payload;
      state.lastFetched = new Date().toISOString();
    });
    builder.addCase(fetchAllNotifications.rejected, (state, { payload }) => {
      state.notificationsLoading = false;
      state.notificationsError = payload;
    });

    // Fetch unread preview
    builder.addCase(fetchUnreadPreview.pending, (state) => {
      state.previewLoading = true;
    });
    builder.addCase(fetchUnreadPreview.fulfilled, (state, { payload }) => {
      state.previewLoading = false;
      state.unreadPreview = payload.unread || payload.notifications || [];
      state.unreadCount = payload.unread_count || 0;
    });
    builder.addCase(fetchUnreadPreview.rejected, (state) => {
      state.previewLoading = false;
    });

    // Mark notification read
    builder.addCase(markNotificationRead.fulfilled, (state, { payload }) => {
      const notifId = payload.id;

      // Update in notifications list
      const notifIndex = state.notifications.findIndex((n) => n.id === notifId);
      if (notifIndex !== -1) {
        state.notifications[notifIndex].isRead = true;
      }

      // Remove from unread preview
      state.unreadPreview = state.unreadPreview.filter((n) => n.id !== notifId);

      // Decrement unread count
      if (state.unreadCount > 0) {
        state.unreadCount -= 1;
      }
    });

    // Mark all read
    builder.addCase(markAllNotificationsRead.fulfilled, (state) => {
      // Mark all notifications as read
      state.notifications = state.notifications.map((n) => ({
        ...n,
        isRead: true,
      }));
      state.unreadPreview = [];
      state.unreadCount = 0;
    });

    // Approve notification
    builder.addCase(approveNotification.pending, (state) => {
      state.actionLoading = true;
      state.actionError = null;
    });
    builder.addCase(approveNotification.fulfilled, (state, { payload }) => {
      state.actionLoading = false;
      const notifIndex = state.notifications.findIndex(
        (n) => n.id === payload.id,
      );
      if (notifIndex !== -1) {
        state.notifications[notifIndex] = {
          ...state.notifications[notifIndex],
          status: "approved",
          isRead: true,
        };
      }
    });
    builder.addCase(approveNotification.rejected, (state, { payload }) => {
      state.actionLoading = false;
      state.actionError = payload;
    });

    // Reject notification
    builder.addCase(rejectNotification.pending, (state) => {
      state.actionLoading = true;
      state.actionError = null;
    });
    builder.addCase(rejectNotification.fulfilled, (state, { payload }) => {
      state.actionLoading = false;
      const notifIndex = state.notifications.findIndex(
        (n) => n.id === payload.id,
      );
      if (notifIndex !== -1) {
        state.notifications[notifIndex] = {
          ...state.notifications[notifIndex],
          status: "rejected",
          isRead: true,
        };
      }
    });
    builder.addCase(rejectNotification.rejected, (state, { payload }) => {
      state.actionLoading = false;
      state.actionError = payload;
    });

    // Delegate notification
    builder.addCase(delegateNotification.pending, (state) => {
      state.actionLoading = true;
      state.actionError = null;
    });
    builder.addCase(delegateNotification.fulfilled, (state, { payload }) => {
      state.actionLoading = false;
      const notifIndex = state.notifications.findIndex(
        (n) => n.id === payload.id,
      );
      if (notifIndex !== -1) {
        state.notifications[notifIndex] = {
          ...state.notifications[notifIndex],
          status: "delegated",
          isRead: true,
        };
      }
    });
    builder.addCase(delegateNotification.rejected, (state, { payload }) => {
      state.actionLoading = false;
      state.actionError = payload;
    });

    // Escalate notification
    builder.addCase(escalateNotification.pending, (state) => {
      state.actionLoading = true;
      state.actionError = null;
    });
    builder.addCase(escalateNotification.fulfilled, (state, { payload }) => {
      state.actionLoading = false;
      const notifIndex = state.notifications.findIndex(
        (n) => n.id === payload.id,
      );
      if (notifIndex !== -1) {
        state.notifications[notifIndex] = {
          ...state.notifications[notifIndex],
          status: "escalated",
          isRead: true,
        };
      }
    });
    builder.addCase(escalateNotification.rejected, (state, { payload }) => {
      state.actionLoading = false;
      state.actionError = payload;
    });

    // Delete notification
    builder.addCase(deleteNotification.fulfilled, (state, { payload }) => {
      const notifId = payload.id;
      state.notifications = state.notifications.filter((n) => n.id !== notifId);
      state.unreadPreview = state.unreadPreview.filter((n) => n.id !== notifId);

      // Clear selection if deleted notification was selected
      if (state.selectedNotificationId === notifId) {
        state.selectedNotificationId = null;
      }
    });

    // Delete all unread
    builder.addCase(deleteAllUnreadNotifications.fulfilled, (state) => {
      state.notifications = state.notifications.filter((n) => n.isRead);
      state.unreadPreview = [];
      state.unreadCount = 0;
    });
  },
});

export const {
  setSelectedNotification,
  clearSelectedNotification,
  setWsConnected,
  addNotificationFromWs,
  updateNotificationFromWs,
  removeNotificationsFromWs,
  setUnreadCount,
  clearNotificationState,
  clearActionError,
} = notificationSlice.actions;

export default notificationSlice.reducer;
